import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { IApplicationState } from '.';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import { ISurveyEditModel, ITranslationEditItem, IValidationErrorCollection } from '../models';
import * as translationsApi from '../services/TranslationsAPI';
import { TranslationHelper } from '../utilities';

export interface ISurveyTitleAdminState {
	isLoadingOrSaving: boolean;
	isError: boolean;
	translations: ITranslationEditItem[];
	validationErrors: IValidationErrorCollection;
	originalTranslations: { [cultureCode: string]: ITranslationEditItem };
}

export const initialState: ISurveyTitleAdminState = {
	isLoadingOrSaving: true,
	isError: false,
	translations: [],
	validationErrors: {},
	originalTranslations: {},
};

export const loadSurveyTitleAdminData = createAsyncThunk(
	'surveyTitleAdmin/loadSurveyTitleAdminData',
	async (surveyId: number, { rejectWithValue }) => {
		try {
			return await translationsApi.getTranslations({
				apiUrl: `${Constants.API_TRANSLATIONS_URL}/${surveyId}/Title`,
				uniqueId: surveyId,
			});
		} catch {
			return rejectWithValue([]);
		}
	},
);

export const saveSurveyTitleAdminData = createAsyncThunk(
	'surveyTitleAdmin/saveSurveyTitleAdminData',
	async (surveyId: number, { getState, rejectWithValue }) => {
		try {
			const {
				surveyTitleAdmin: { translations },
				survey: { surveyEditModel },
			} = getState() as IApplicationState;
			return await translationsApi.saveTranslations(
				{ apiUrl: `${Constants.API_TRANSLATIONS_URL}/${surveyId}/Title`, uniqueId: surveyId },
				surveyEditModel.defaultCultureCode,
				translations,
			);
		} catch {
			return rejectWithValue([]);
		}
	},
);

const surveyTitleAdminSlice = createSlice({
	name: `surveyTitleAdmin`,
	initialState: initialState,
	reducers: {
		updateSurveyTitleTranslation: (
			state,
			action: PayloadAction<Pick<ITranslationEditItem, 'cultureCode' | 'textValue'> & Pick<ISurveyEditModel, 'defaultCultureCode'>>,
		) => {
			const {
				payload: { cultureCode, defaultCultureCode, textValue },
			} = action;

			for (const translation of state.translations) {
				if (translation.cultureCode !== cultureCode) {
					continue;
				}

				translation.textValue = textValue;
				translation.editState = TranslationHelper.getEditState(translation, state.originalTranslations[cultureCode]);

				if (cultureCode === defaultCultureCode) {
					if (/\S+/.test(textValue)) {
						delete state.validationErrors[defaultCultureCode];
					} else {
						state.validationErrors[defaultCultureCode] = [
							ContentConstants.VALIDATION_MESSAGE_REQUIRED_FORM_FIELD.replace('{label}', 'Title'),
						];
					}
				}
				break;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(isAnyOf(loadSurveyTitleAdminData.pending, saveSurveyTitleAdminData.pending), (state) => {
				state.isLoadingOrSaving = true;
				state.isError = false;
				state.validationErrors = {};
			})
			.addMatcher(isAnyOf(loadSurveyTitleAdminData.fulfilled, saveSurveyTitleAdminData.fulfilled), (state, action) => {
				state.isLoadingOrSaving = false;
				state.translations = action.payload;
				state.originalTranslations = state.translations.reduce((map, value) => {
					map[value.cultureCode] = value;
					return map;
				}, {});
			})
			.addMatcher(isAnyOf(loadSurveyTitleAdminData.rejected, saveSurveyTitleAdminData.rejected), (state, action) => {
				state.isLoadingOrSaving = false;
				state.isError = true;
				state.validationErrors = action.payload as IValidationErrorCollection;
			});
	},
});

export const { updateSurveyTitleTranslation } = surveyTitleAdminSlice.actions;
export default surveyTitleAdminSlice.reducer;
